import { normalizeShortCode, OFFLINE_DATABASE_VERSION } from "../shared/short-code";
import type { Registration, Redemption, RedemptionScanResponse } from "./types";

export interface PendingRedemption {
  id: string;
  code: string;
  redemption: Redemption;
  conflict?: string;
}
export interface DeviceState {
  preparedAt?: string;
  lastSync?: string;
  enabled?: boolean;
}
let database: Promise<IDBDatabase> | undefined;
function openDatabase() {
  return database ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open("petexpo26-offline", OFFLINE_DATABASE_VERSION);
    request.onupgradeneeded = () => {
      // Explicit event reset: remove the old participant cache AND queued test handouts.
      for (const name of Array.from(request.result.objectStoreNames)) request.result.deleteObjectStore(name);
      request.result.createObjectStore("registrations", { keyPath: "id" });
      request.result.createObjectStore("outbox", { keyPath: "id" });
      request.result.createObjectStore("meta");
    };
    request.onblocked = () => reject(new Error("Zamknij pozostałe karty panelu i otwórz aplikację ponownie, aby wyczyścić starą bazę."));
    request.onsuccess = () => { request.result.onversionchange = () => request.result.close(); resolve(request.result); };
    request.onerror = () => reject(new Error("Pamięć offline jest niedostępna. Nie wydawaj nagród bez zapisu."));
  });
}
async function transaction<T>(stores: string[], mode: IDBTransactionMode, work: (tx: IDBTransaction, done: (value: T) => void) => void): Promise<T> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(stores, mode);
    let value: T;
    tx.oncomplete = () => resolve(value);
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error("Nie udało się zapisać danych na iPadzie. Nie wydawaj nagrody."));
    try { work(tx, result => { value = result; }); }
    catch (error) { tx.abort(); reject(error); }
  });
}
export function deviceState(): Promise<DeviceState> {
  return transaction(["meta"], "readonly", (tx, done) => {
    const req = tx.objectStore("meta").get("device");
    req.onsuccess = () => done(req.result ?? {});
  });
}
export function cachedRegistrations(): Promise<Registration[]> {
  return transaction(["registrations"], "readonly", (tx, done) => {
    const req = tx.objectStore("registrations").getAll();
    req.onsuccess = () => done(req.result);
  });
}
export function pendingRedemptions(): Promise<PendingRedemption[]> {
  return transaction(["outbox"], "readonly", (tx, done) => {
    const req = tx.objectStore("outbox").getAll();
    req.onsuccess = () => done(req.result);
  });
}
// Store only what the offline scanner needs; contact and marketing data stay online.
function minimal(record: Registration): Registration {
  return { id: record.id, name: record.name, createdAt: record.createdAt, signedQr: record.signedQr, provisional: record.provisional, redemption: record.redemption ?? null };
}
export function remember(records: Registration[], prepareDevice = false): Promise<void> {
  return transaction(["registrations", "meta"], "readwrite", (tx, done) => {
    const store = tx.objectStore("registrations");
    for (const record of records) {
      const req = store.get(record.id);
      req.onsuccess = () => store.put({ ...minimal(record), signedQr: record.signedQr ?? req.result?.signedQr, redemption: req.result?.redemption ?? record.redemption ?? null });
    }
    if (prepareDevice) tx.objectStore("meta").put({ enabled: true, preparedAt: new Date().toISOString(), lastSync: new Date().toISOString() } satisfies DeviceState, "device");
    done();
  });
}
export function clearOffline(): Promise<void> {
  return transaction(["registrations", "meta", "outbox"], "readwrite", (tx, done) => {
    const req = tx.objectStore("outbox").count();
    req.onsuccess = () => {
      if (req.result) return tx.abort();
      tx.objectStore("registrations").clear();
      tx.objectStore("meta").clear();
    };
    done();
  });
}
export async function scanLocally(code: string): Promise<RedemptionScanResponse> {
  if (!(await deviceState()).enabled) throw new Error("Najpierw zaloguj się online i przygotuj ten iPad do pracy offline.");
  const id = normalizeShortCode(code);
  if (!id) throw new Error("Nieprawidłowy format. Nowy kod ma 17 znaków i zaczyna się od P.");
  return transaction(["registrations"], "readonly", (tx, done) => {
    const req = tx.objectStore("registrations").get(id);
    req.onsuccess = () => {
      const registration: Registration = req.result ?? { id, name: `Uczestnik ${id}`, createdAt: "", provisional: true };
      done({ outcome: registration.redemption ? "redeemed" : "ready", registration, redemption: registration.redemption, provisional: registration.provisional });
    };
  });
}
export async function queueRedemption(code: string, legacy?: Registration): Promise<RedemptionScanResponse> {
  if (!(await deviceState()).enabled) throw new Error("Panel offline nie jest przygotowany.");
  // Operator-selected policy: well-formed, unseen IDs are accepted provisionally.
  const id = normalizeShortCode(code);
  const ticket = id ? (await scanLocally(id)).registration : legacy;
  if (!ticket) throw new Error("Nieprawidłowy kod QR.");
  return transaction(["registrations", "outbox"], "readwrite", (tx, done) => {
    const store = tx.objectStore("registrations");
    const req = store.get(ticket.id);
    req.onsuccess = () => {
      const registration: Registration = { ...minimal(ticket), ...req.result };
      if (registration.redemption) {
        done({ outcome: "redeemed", registration, redemption: registration.redemption, alreadyRedeemed: true });
        return;
      }
      const redemption: Redemption = { redemptionId: crypto.randomUUID(), registrationId: ticket.id, registrationCreatedAt: ticket.createdAt, redeemedAt: new Date().toISOString() };
      registration.redemption = redemption;
      store.put(registration);
      tx.objectStore("outbox").add({ id: ticket.id, code, redemption } satisfies PendingRedemption);
      done({ outcome: "redeemed", registration, redemption, alreadyRedeemed: false });
    };
  });
}
export async function apiFetch(path: string, init?: RequestInit) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try { return await fetch(path, { ...init, signal: controller.signal }); }
  catch { throw new Error("Brak połączenia z serwerem. Zapisane odbiory pozostają na iPadzie."); }
  finally { clearTimeout(timer); }
}
let syncing = false;
export async function syncPending(): Promise<void> {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  try {
    for (const pending of await pendingRedemptions()) {
      if (pending.conflict) continue;
      const response = await apiFetch("/api/admin/redemptions/redeem", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: pending.code, redemptionId: pending.redemption.redemptionId, redeemedAt: pending.redemption.redeemedAt }),
      });
      if (response.status === 401) throw new Error("Zaloguj się ponownie, aby wysłać odbiory. Zapisane dane pozostają na iPadzie.");
      const payload = await response.json() as RedemptionScanResponse;
      if (response.status === 400 && payload.outcome === "invalid") {
        await transaction<void>(["outbox"], "readwrite", (tx, done) => {
          tx.objectStore("outbox").put({ ...pending, conflict: payload.error || `Brak rejestracji: ${pending.id}` });
          done();
        });
        continue;
      }
      if ((!response.ok && response.status !== 409) || !payload.registration || !payload.redemption || payload.redemption.registrationId !== pending.id || (response.ok && payload.redemption.redemptionId !== pending.redemption.redemptionId)) throw new Error(payload.error || "Brak potwierdzenia zapisu. Odbiory pozostają na iPadzie.");
      await transaction<void>(["registrations", "outbox", "meta"], "readwrite", (tx, done) => {
        tx.objectStore("registrations").put(minimal({ ...payload.registration!, redemption: payload.redemption }));
        if (response.status === 409) {
          tx.objectStore("outbox").put({ ...pending, conflict: `Serwer ma już inny odbiór: ${payload.registration!.name}. Pobierz kopię i sprawdź wydanie.` });
        } else tx.objectStore("outbox").delete(pending.id);
        const req = tx.objectStore("meta").get("device");
        req.onsuccess = () => tx.objectStore("meta").put({ ...req.result, lastSync: new Date().toISOString() }, "device");
        done();
      });
    }
  } finally { syncing = false; }
}
